import { useState, useEffect } from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { ArrowRight, ChevronDown, Menu, Sparkles } from "lucide-react";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
interface HeaderMenuItem {
  id?: string | number;
  label: string;
  href?: string;
  children?: HeaderMenuItem[];
}
function DesktopDropdownMenu({
  items
}: {
  items: HeaderMenuItem[];
}) {
  return <div className="bg-white rounded-lg shadow-md border border-gray-100 py-1.5 px-1 flex flex-col gap-0.5 min-w-[210px]">
      {items.map((child, childIdx) => {
      const hasGrandchildren = Boolean(child.children && child.children.length > 0);
      return <div key={child.id || child.label} data-index={childIdx} className="relative group/sub">
            <Link to={child.href || "#"} className="rounded-md px-3 py-2 text-sm text-slate-900 hover:text-slate-600 hover:bg-black/5 flex items-center justify-between gap-2 text-left leading-snug transition-colors duration-150">
              <span>{child.label}</span>
              {hasGrandchildren && <ChevronDown className="size-3 text-slate-500 -rotate-90" aria-hidden="true" />}
            </Link>
            {hasGrandchildren && child.children && <div className="invisible opacity-0 group-hover/sub:visible group-hover/sub:opacity-100 transition-all duration-150 absolute top-0 left-full pl-1 z-50">
                <DesktopDropdownMenu items={child.children} />
              </div>}
          </div>;
    })}
    </div>;
}
function MobileNavItem({
  item,
  index,
  onClose,
  level = 0
}: {
  item: HeaderMenuItem;
  index: number;
  onClose: () => void;
  level?: number;
}) {
  const [expanded, setExpanded] = useState(false);
  const hasChildren = Boolean(item.children && item.children.length > 0);
  if (!hasChildren) {
    return <Link to={item.href || "#"} data-index={index} onClick={onClose} className={`flex items-center justify-between rounded-lg px-3 py-2.5 text-foreground hover:bg-muted/70 transition-colors ${level > 0 ? "text-sm text-foreground/85 pl-4" : "text-base font-medium"}`}>
        <span>{item.label}</span>
        <ArrowRight className="w-4 h-4 opacity-40 shrink-0" />
      </Link>;
  }
  return <div data-index={index} className="flex flex-col">
      <Button variant="ghost" type="button" onClick={() => setExpanded(prev => !prev)} className={`flex h-auto w-full items-center justify-between rounded-lg px-3 py-2.5 text-foreground hover:bg-muted/70 transition-colors text-left font-normal ${level > 0 ? "text-sm text-foreground/85 pl-4" : "text-base font-medium"}`} aria-expanded={expanded}>
        <span>{item.label}</span>
        <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform duration-200 shrink-0 ${expanded ? "rotate-180" : ""}`} aria-hidden="true" />
      </Button>
      {expanded && item.children && <div className="flex flex-col gap-1 pl-3 my-1 border-l-2 border-border/50 ml-3">
          {item.children.map((child, childIdx) => <MobileNavItem key={child.id || child.label} item={child} index={childIdx} onClose={onClose} level={level + 1} />)}
        </div>}
    </div>;
}
function HeaderContent() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const {
    menuItems
  } = useMenu();
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 15);
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  return <header id="header" className={`sticky top-0 z-50 w-full border-b border-gray-200 transition-colors duration-200 ${scrolled ? "bg-[#edf2f0]" : "bg-[#f4f7f6]"}`}>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 xl:h-20 items-center justify-between gap-3">
          <Link to="/" className="group flex items-center gap-3 transition-transform duration-200 active:scale-98 shrink-0" aria-label="PhysAnalytica home">
            <div className="relative flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-blue-500/10 via-emerald-500/10 to-teal-500/10 p-1.5 ring-1 ring-border/60 transition-colors group-hover:ring-primary/40">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1754222/img/Phys-analytica-Logo-without-wording-1.png" alt="PhysAnalytica logo" className="w-full h-auto object-contain max-h-7" />
            </div>
            <div className="flex flex-col">
              <div className="flex items-baseline tracking-tight font-black text-xl lg:text-2xl">
                <span className="text-[#1D63A8] transition-colors group-hover:text-[#164e85]">Phys</span>
                <span className="text-[#3E8E53] transition-colors group-hover:text-[#327343]">Analytica</span>
              </div>
              <p className="hidden sm:block lg:hidden text-[10px] sm:text-xs text-muted-foreground leading-tight max-w-xs line-clamp-2">
                Physiotherapy, Biomechanics, and Movement Healthcare. Dedicated clinical precision and evidence-based assessment for elite movement, daily recovery, and corporate wellness.
              </p>
            </div>
          </Link>

          <nav aria-label="Main Navigation" className="hidden lg:flex items-center gap-6 2xl:gap-8 text-sm font-medium">
            {menuItems.map((item, i) => {
            const hasChildren = Boolean(item.children && item.children.length > 0);
            if (hasChildren && item.children) {
              return <div key={item.id || item.label} data-index={i} className="relative group inline-flex items-center">
                    <Link to={item.href || "#"} className="rounded-lg px-2 py-1.5 text-slate-900 hover:text-slate-600 hover:bg-black/5 text-center leading-tight inline-flex items-center justify-center gap-1 transition-colors duration-150">
                      <span>{item.label}</span>
                      <ChevronDown className="size-3.5 text-slate-700 transition-transform duration-200 group-hover:rotate-180" aria-hidden="true" />
                    </Link>
                    <div className="invisible opacity-0 group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100 transition-all duration-150 absolute top-full left-0 pt-1.5 z-50">
                      <DesktopDropdownMenu items={item.children} />
                    </div>
                  </div>;
            }
            return <Link key={item.id || item.label} to={item.href || "#"} data-index={i} className="rounded-lg px-2 py-1.5 text-slate-900 hover:text-slate-600 hover:bg-black/5 text-center leading-tight max-w-[130px] inline-flex items-center justify-center transition-colors duration-150">
                  {item.label}
                </Link>;
          })}
          </nav>

          <div className="flex items-center gap-2 sm:gap-3 shrink-0">
            <Button asChild className="rounded-full h-auto px-4 sm:px-5 py-1.5 bg-[#5b9542] hover:bg-[#4d8037] text-white text-xs sm:text-sm font-bold tracking-wide transition-all duration-200 active:scale-95 shadow-xs">
              <Link to="https://physanalytica.com/book-now/" className="flex flex-col items-center justify-center text-center leading-tight">
                <span>Book</span>
                <span>Now</span>
              </Link>
            </Button>

            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild className="lg:hidden">
                <Button variant="outline" size="icon" className="rounded-xl border-border/80 text-foreground" aria-label="Open navigation menu">
                  <Menu className="w-4 h-4" />
                  <span className="sr-only">Menu</span>
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[310px] sm:w-[380px] p-6 flex flex-col justify-between">
                <div className="flex flex-col gap-6">
                  <SheetHeader className="text-left pb-4 border-b border-border/60">
                    <SheetTitle>
                      <Link to="/" onClick={() => setMobileOpen(false)} className="flex items-center gap-2.5">
                        <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1754222/img/Phys-analytica-Logo-without-wording-1.png" alt="PhysAnalytica logo" className="h-7 w-auto object-contain" />
                        <span className="font-black text-xl tracking-tight text-[#1D63A8]">
                          Phys<span className="text-[#3E8E53]">Analytica</span>
                        </span>
                      </Link>
                    </SheetTitle>
                  </SheetHeader>

                  <nav aria-label="Mobile Navigation" className="flex flex-col gap-1">
                    <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground px-2 mb-1">
                      Navigation
                    </span>
                    {menuItems.map((item, i) => <MobileNavItem key={item.id || item.label} item={item} index={i} onClose={() => setMobileOpen(false)} />)}
                  </nav>
                </div>

                <div className="pt-6 border-t border-border/60 flex flex-col gap-3">
                  <Button asChild className="w-full rounded-xl bg-[#5b9542] hover:bg-[#4d8037] text-white py-5 font-semibold">
                    <Link to="https://physanalytica.com/book-now/" onClick={() => setMobileOpen(false)} className="flex items-center justify-center gap-2 font-medium">
                      <Sparkles className="w-4 h-4" />
                      <span>Book Now</span>
                    </Link>
                  </Button>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>;
}
export default function Header() {
  return <MenuProvider menu_id="36">
      <HeaderContent />
    </MenuProvider>;
}